import React, { useState } from 'react';
import { Paper, Table, Text, NumberInput } from '@mantine/core';
import { useGetCustomBillomatArticlesConfigQuery } from '../graphql/queries/getCustomBillomatArticlesConfig.generated';
import { useUpdateCustomBillomatArticlesConfigMutation } from '../graphql/mutations/updateCustomerBillomatArtilces.generated';
import { useQuery } from '@apollo/client';
import { allUserCountriesQuery } from '../graphql/queries';
import i18n from '@/utils/i18n';
import { IconButton } from '../components/Button/Button';
import { FaSave } from 'react-icons/fa';
import ClassicLoadingSpinner from '../components/LoadingSpinner/ClassicLoadingSpinner';
import { ContentSection } from '../components/Layout/ContentSection';

export const EditCustomBillomatArticles = () => {
  const [articles, setArticles] = useState<
    Record<string, Record<string, number>>
  >({});
  const { data: countriesData = { __type: { enumValues: [] } } } = useQuery(
    allUserCountriesQuery,
  );
  const countries = countriesData.__type.enumValues.map(
    (country: any) => country.name,
  );

  const { loading } = useGetCustomBillomatArticlesConfigQuery({
    onCompleted: (data) => {
      const parsedData = JSON.parse(
        data.getCustomBillomatArticlesConfig || '{}',
      );
      const { __typename, id, updated_at, ...cleanedData } = parsedData;
      extendArticlesWithMissingCountries(cleanedData);
    },
    fetchPolicy: 'network-only',
  });

  const [updateCustomBillomatArticlesConfig] =
    useUpdateCustomBillomatArticlesConfigMutation();

  function extendArticlesWithMissingCountries(
    data: Record<string, Record<string, number>> = articles,
  ) {
    const allCountries = countries;
    for (const country of allCountries) {
      for (const article of Object.entries(data)) {
        if (!article[1][country]) {
          article[1][country] = article[1].DE;
        }
      }
    }
    setArticles(data);
  }

  function updateArticles(article: string, country: string, value: number) {
    const data = { ...articles };
    data[article][country] = value;
    setArticles(data);
  }

  function save() {
    updateCustomBillomatArticlesConfig({
      variables: {
        update: JSON.stringify(articles),
      },
    });
  }

  if (loading) return <ClassicLoadingSpinner />;

  return (
    <ContentSection
      title={i18n.t('Common:customBillomatArticles')}
      collapsable={false}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton
          icon={<FaSave />}
          tag="a"
          onClick={save}
          className="my-1 me-3"
          label={i18n.t('Common:save')}
        />
      </div>
      <Paper
        style={{
          padding: 'md',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 400px)',
        }}
        shadow="xs"
        className="mt-3 mb-3"
      >
        <Table>
          <thead
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: 'white',
            }}
          >
            <tr>
              <th
                style={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  backgroundColor: 'white',
                }}
              >
                <Text weight={700}>Article</Text>
              </th>
              {countries.map((country: string) => (
                <th key={country}>
                  <Text weight={700}>
                    {i18n.t(`Common:Country:${country}`)}
                  </Text>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(articles)
              .filter((r) => Object.keys(typeof r[1] !== 'string'))
              .map((row) => (
                <tr key={row[0]}>
                  <td
                    style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      backgroundColor: 'white',
                    }}
                  >
                    {row[0]}
                  </td>
                  {countries.map((country: string) => (
                    <td key={country}>
                      <NumberInput
                        style={{ width: '10rem' }}
                        value={row[1][country]}
                        onChange={(v) => {
                          const parsedNumber = parseInt(v as string);
                          if (!Number.isNaN(parsedNumber)) {
                            updateArticles(row[0], country, parsedNumber);
                          }
                        }}
                        placeholder="Enter a number..."
                        min={0}
                      />
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </Table>
      </Paper>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton
          icon={<FaSave />}
          tag="a"
          onClick={save}
          className="my-1 me-3"
          label={i18n.t('Common:save')}
        />
      </div>
    </ContentSection>
  );
};
